import { num } from '../../scene/component-types.js';
import type { SettingsSection } from '../settings-types.js';

/** Defaults mirror prism_core::player (PlayerSettings, ScriptingSettings, LimitsSettings). */
const player: SettingsSection = {
  key: 'player',
  label: 'Player',
  icon: 'play',
  order: 10,
  hint: 'Game window of the exported game / prism run',
  fields: [
    { key: 'title', label: 'Window title', type: { kind: 'string', placeholder: 'project name' } },
    { key: 'width', label: 'Width', type: num(1, { min: 1, max: 16384, integer: true }), read: () => 1280, hint: 'windowed size in pixels' },
    { key: 'height', label: 'Height', type: num(1, { min: 1, max: 16384, integer: true }), read: () => 720 },
    {
      key: 'fullscreen',
      label: 'Fullscreen mode',
      type: { kind: 'enum', options: ['windowed', 'borderless', 'exclusive'] },
      read: () => 'windowed',
      hint: 'borderless = fullscreen window; exclusive changes the video mode',
    },
    { key: 'resizable', label: 'Resizable', type: { kind: 'bool' }, read: () => true },
    {
      key: 'autoPlay',
      label: 'Auto play',
      type: { kind: 'bool' },
      read: () => true,
      hint: 'off: the start scene waits paused until the first key or click',
    },
    { key: 'startScene', label: 'Start scene', type: { kind: 'path', suffix: '.scene.json' }, hint: 'empty = the project startScene' },
    { key: 'icon', label: 'Icon', type: { kind: 'path', suffix: '.png' }, hint: 'window icon (PNG)' },
    {
      key: 'cursorVisible',
      label: 'Cursor visible',
      type: { kind: 'bool' },
      read: () => true,
      hint: 'scripts: set_cursor_visible / set_cursor_locked',
    },
    {
      key: 'saveLocation',
      label: 'Save location',
      type: { kind: 'enum', options: ['appData', 'gameFolder'] },
      read: () => 'appData',
      hint: 'appData = per-user data folder; gameFolder = saves/ next to the game',
    },
    { key: 'saveFolder', label: 'Save folder override', type: { kind: 'string', placeholder: 'use save location above' }, hint: 'Absolute folder or a folder relative to the exported game' },
    {
      key: 'graphicsApi',
      label: 'Graphics API',
      type: { kind: 'enum', options: ['dx12', 'vulkan'] },
      read: () => 'dx12',
      hint: 'exported game renders with DirectX 12 unless Vulkan is chosen; falls back to the other if unavailable',
    },
  ],
};

const scripting: SettingsSection = {
  key: 'scripting',
  label: 'Scripting',
  icon: 'script',
  order: 20,
  fields: [
    {
      key: 'maxOperations',
      label: 'Max operations',
      type: num(100000, { min: 0, integer: true }),
      read: () => 5000000,
      hint: 'Rhai operations per callback before it is stopped (0 = unlimited)',
    },
    {
      key: 'maxConsecutiveErrors',
      label: 'Max consecutive errors',
      type: num(1, { min: 0, integer: true }),
      read: () => 50,
      hint: 'failing callbacks in a row before a script is disabled (0 = never)',
    },
  ],
};

const limits: SettingsSection = {
  key: 'limits',
  label: 'Engine limits',
  icon: 'warning',
  order: 30,
  fields: [
    {
      key: 'maxPrefabDepth',
      label: 'Max prefab depth',
      type: num(1, { min: 1, max: 64, integer: true }),
      read: () => 8,
      hint: 'how deeply prefabs may nest (cycles are always refused)',
    },
  ],
};

export default [player, scripting, limits];
